import React, { useCallback, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  Area, Bar, Brush, CartesianGrid, Cell, ComposedChart, Line, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis
} from 'recharts';
import {
  AXIS_TICK, BORDER, ChartCard, ChartSkeleton, NoData, SeriesLegend, SeriesToggle, Stat, StatStrip, TipBox, TipRow, TooltipInjected, tooltipPoint
} from '../../OnlineJournal/components/analytics/chartKit';
import { CHART } from '../../OnlineJournal/constants';
import { fmtInt, fmtPct, share } from '../../OnlineJournal/format';
import { useLoad } from '../../OnlineJournal/hooks/useLoad';
import { analyticsApi } from './analyticsApi';
import { bucketLabel, bucketRange, bucketTitle, dm, EVENT_COLORS, KIND_COLORS, KIND_LABELS } from './analyticsFormat';
import { BRUSH_FROM, ByToggle, HEIGHT, MiniToggle, toggleIn, useBucketBy } from './analyticsKit';
import { BucketBy, TimelineBucket, TimelineResponse, ViewProps } from './analyticsTypes';

type Mode = 'bars' | 'cum';

interface Point extends TimelineBucket {
  cumCreated: number;
  cumDone: number;
  cumErrors: number;
}

const STACK: SeriesToggle[] = [
  { key: 'direct', label: KIND_LABELS.direct, color: KIND_COLORS.direct },
  { key: 'via', label: KIND_LABELS.via, color: KIND_COLORS.via },
  { key: 'return', label: KIND_LABELS.return, color: KIND_COLORS.return }
];
const EVENTS: SeriesToggle[] = [
  { key: 'done', label: 'Завершено (по дате «Завершен»)', color: EVENT_COLORS.done, line: true },
  { key: 'errors', label: 'Ошибки (события)', color: EVENT_COLORS.errors, line: true }
];
const CUM: SeriesToggle[] = [
  { key: 'cumCreated', label: 'Создано накопительно', color: CHART.cum },
  { key: 'cumDone', label: 'Завершено накопительно', color: EVENT_COLORS.done, line: true },
  { key: 'cumErrors', label: 'Ошибки накопительно', color: EVENT_COLORS.errors, line: true }
];

const Tip: React.FC<TooltipInjected<Point> & { by?: BucketBy; mode?: Mode }> = props => {
  const p = tooltipPoint(props);
  if (!p) return null;
  const by = props.by || 'day';
  const rows: TipRow[] = [
    { label: 'Создано', value: fmtInt(p.created), bold: true },
    ...STACK.map(s => {
      const v = p[s.key as 'direct' | 'via' | 'return'];
      return { label: s.label, value: fmtInt(v), color: s.color, hint: p.created ? fmtPct(share(v, p.created)) : undefined };
    }),
    { label: 'Завершено', value: fmtInt(p.done), color: EVENT_COLORS.done, divider: true },
    { label: 'Ошибок', value: fmtInt(p.errors), color: EVENT_COLORS.errors }
  ];
  if (props.mode === 'cum') {
    rows.push(
      { label: 'Создано накопительно', value: fmtInt(p.cumCreated), color: CHART.cum, divider: true },
      { label: 'Завершено накопительно', value: fmtInt(p.cumDone) },
      { label: 'Ошибок накопительно', value: fmtInt(p.cumErrors) }
    );
  }
  return (
    <TipBox
      width={280} title={bucketTitle(p.date, by, p.partial)} rows={rows}
      footer={p.created ? `Клик по столбцу - открыть документы, созданные в ${by === 'week' ? 'эту неделю' : 'этот день'}` : undefined}
    />
  );
};

const Chart: React.FC<{ data: TimelineResponse; onCohort: ViewProps['onCohort'] }> = ({ data, onCohort }) => {
  const [mode, setMode] = useState<Mode>('bars');
  const [hidden, setHidden] = useState<string[]>([]);
  const toggle = useCallback((key: string) => setHidden(h => toggleIn(h, key)), []);
  const show = (key: string) => hidden.indexOf(key) === -1;
  const by = data.by;
  const daily = by === 'day';

  const points = useMemo<Point[]>(() => {
    let created = 0;
    let done = 0;
    let errors = 0;
    return data.buckets.map(b => {
      created += b.created;
      done += b.done;
      errors += b.errors;
      return { ...b, cumCreated: created, cumDone: done, cumErrors: errors };
    });
  }, [data.buckets]);

  const stats = useMemo<Stat[]>(() => {
    const t = data.buckets.reduce((acc, b) => ({
      created: acc.created + b.created, direct: acc.direct + b.direct, via: acc.via + b.via, ret: acc.ret + b.return,
      done: acc.done + b.done, errors: acc.errors + b.errors, withData: acc.withData + (b.created ? 1 : 0)
    }), { created: 0, direct: 0, via: 0, ret: 0, done: 0, errors: 0, withData: 0 });
    const peak = data.buckets.reduce<TimelineBucket | null>((best, b) => (!best || b.created > best.created ? b : best), null);
    const moves = t.direct + t.via;
    return [
      { label: 'Создано', value: fmtInt(t.created), hint: `перемещений ${fmtInt(moves)} · возвратов ${fmtInt(t.ret)}` },
      {
        label: daily ? 'В среднем за день' : 'В среднем за неделю',
        value: (data.buckets.length ? t.created / data.buckets.length : 0).toLocaleString('ru-RU', { maximumFractionDigits: 1 }),
        hint: `${daily ? 'дней' : 'недель'} с документами: ${t.withData} из ${data.buckets.length}`
      },
      {
        label: 'Пик', value: peak && peak.created ? fmtInt(peak.created) : '-',
        hint: peak && peak.created ? bucketLabel(peak.date, by) : 'документов нет'
      },
      {
        label: 'Завершено', value: fmtInt(t.done), color: EVENT_COLORS.done, hint: `${fmtPct(share(t.done, t.created))} к созданным`,
        tooltip: 'Сколько документов получили статус «Завершен» в периоде (по дате этого статуса). Среди них могут быть созданные раньше периода, поэтому доля к созданным приблизительна'
      },
      {
        label: 'Ошибок', value: fmtInt(t.errors), color: t.errors ? EVENT_COLORS.errors : undefined, hint: 'события «Ошибка остатка» и «Ошибка 1С»',
        tooltip: 'Сколько раз документы попадали в статусы «Ошибка остатка» и «Ошибка 1С» в периоде. Один документ может получить ошибку несколько раз'
      },
      { label: 'Доля через РЦ', value: fmtPct(share(t.via, moves)), color: KIND_COLORS.via, hint: `${fmtInt(t.via)} из ${fmtInt(moves)} перемещений` }
    ];
  }, [data.buckets, by, daily]);

  if (!points.length || !points.some(p => p.created || p.done || p.errors)) {
    return <NoData height={HEIGHT} text="За период нет документов и событий по выбранным фильтрам" />;
  }

  const legend = mode === 'bars' ? STACK.concat(EVENTS) : CUM;
  const open = (item: { payload?: unknown }) => {
    const p = item.payload as Point | undefined;
    if (p && p.created) onCohort({}, bucketRange(p.date, by, data.period));
  };

  return (
    <Box>
      <StatStrip items={stats} />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <SeriesLegend items={legend} hidden={hidden} onToggle={toggle} />
        <Box sx={{ mb: 1 }}>
          <MiniToggle<Mode>
            value={mode} onChange={setMode}
            options={[{ value: 'bars', label: daily ? 'По дням' : 'По неделям' }, { value: 'cum', label: 'Накопительно' }]}
          />
        </Box>
      </Box>
      <Box sx={{ height: HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={points} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
            <CartesianGrid stroke={CHART.grid} vertical={false} />
            <XAxis dataKey="date" tick={AXIS_TICK} tickFormatter={d => dm(String(d))} axisLine={{ stroke: BORDER }} tickLine={false} minTickGap={14} />
            <YAxis yAxisId="left" tick={AXIS_TICK} tickFormatter={v => fmtInt(Number(v))} axisLine={false} tickLine={false} width={52} allowDecimals={false} />
            <ChartTooltip content={<Tip by={by} mode={mode} />} cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
            {mode === 'bars' && STACK.map(s => (show(s.key) ? (
              <Bar
                key={s.key} yAxisId="left" dataKey={s.key} stackId="k" fill={s.color} isAnimationActive={false} maxBarSize={40}
                onClick={open} style={{ cursor: 'pointer' }}
              >
                {points.map(p => <Cell key={p.date} fillOpacity={p.partial ? 0.55 : 1} />)}
              </Bar>
            ) : null))}
            {mode === 'bars' && EVENTS.map(s => (show(s.key) ? (
              <Line key={s.key} yAxisId="left" dataKey={s.key} type="monotone" stroke={s.color} strokeWidth={2} dot={false} isAnimationActive={false} />
            ) : null))}
            {mode === 'cum' && show('cumCreated') && (
              <Area yAxisId="left" dataKey="cumCreated" type="monotone" stroke={CHART.cum} fill={CHART.cum} fillOpacity={0.12} strokeWidth={2} isAnimationActive={false} />
            )}
            {mode === 'cum' && show('cumDone') && (
              <Line yAxisId="left" dataKey="cumDone" type="monotone" stroke={EVENT_COLORS.done} strokeWidth={2} dot={false} isAnimationActive={false} />
            )}
            {mode === 'cum' && show('cumErrors') && (
              <Line yAxisId="left" dataKey="cumErrors" type="monotone" stroke={EVENT_COLORS.errors} strokeWidth={2} dot={false} isAnimationActive={false} />
            )}
            {points.length > BRUSH_FROM && (
              <Brush key={points.length} dataKey="date" height={22} stroke="#94a3b8" travellerWidth={8} startIndex={0} tickFormatter={d => dm(String(d))} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

/** Динамика: создание документов по видам, завершение и ошибки по дате события */
const TimelineView: React.FC<ViewProps & { days: number }> = ({ params, paramsKey, active, reloadToken, onCohort, days }) => {
  const [by, setBy] = useBucketBy(days);
  const load = useLoad(
    active ? (_silent: boolean, signal: AbortSignal) => analyticsApi.timeline(params, by, signal) : null,
    `timeline|${by}|${paramsKey}`, reloadToken, 'Не удалось построить динамику документов'
  );
  const data = load.data;

  return (
    <ChartCard
      wide title="Динамика документов"
      subtitle="Столбцы - документы, созданные в периоде, по видам; линии - завершение и ошибки по дате события. Бледный столбец - неделя, которая входит в период частично."
      loading={load.loading} error={load.error ? load.error.text : null} onRetry={load.retry}
      actions={<ByToggle value={by} onChange={setBy} />}
    >
      {!data && !load.error && <ChartSkeleton height={HEIGHT + 120} />}
      {data && <Chart data={data} onCohort={onCohort} />}
      {data && (
        <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', mt: 1 }}>
          Легенда переключает ряды. Клик по столбцу открывает документы этого дня или недели в списке.
        </Typography>
      )}
    </ChartCard>
  );
};

export default React.memo(TimelineView);
